import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Shield, CheckSquare, Square, ChevronDown, X } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES, PERMISSION_MODULES, apiError, grantsNothing, NO_ACCESS_HINT } from './roles';

const KNOWN = new Set(PERMISSION_MODULES.map(m => m.key));

// Stored permissions the checkboxes above do not show. Kept rather than
// silently dropped on save, and listed so an administrator can see them.
const OTHER_LABELS = {
  accounts: 'Accounts — both entities (legacy)',
  users:    'Company settings',
  crm:      'CRM (retired)',
};

export default function UserDetail() {
  const { userId } = useParams();
  const { user: me } = useAuth();
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [message, setMessage] = useState('');

  const accept = (data) => {
    setUser(data);
    setPermissions(data.permissions || []);
    setRole(data.role || 'staff');
  };

  useEffect(() => {
    api.get(`/api/users/${userId}`)
      .then(r => accept(r.data))
      .catch(e => setLoadError(apiError(e, 'User not found')))
      .finally(() => setLoading(false));
  }, [userId]);

  const flash = (kind, text) => {
    setMessage(`${kind}:${text}`);
    setTimeout(() => setMessage(''), 5000);
  };

  const togglePerm = (key) => {
    setPermissions(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const savePermissions = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put(`/api/users/${userId}/permissions`, { permissions });
      accept(res.data);
      flash('success', 'Permissions saved');
    } catch (e) {
      flash('error', apiError(e, 'Failed to save permissions'));
    } finally {
      setSaving(false);
    }
  };

  const saveRole = async () => {
    setSavingRole(true);
    setMessage('');
    try {
      const res = await api.put(`/api/users/${userId}`, { role });
      accept(res.data);
      flash('success', 'Role updated');
    } catch (e) {
      setRole(user.role);
      flash('error', apiError(e, 'Failed to update role'));
    } finally {
      setSavingRole(false);
    }
  };

  const toggleStatus = async () => {
    const endpoint = user.is_active ? `/api/users/${userId}/deactivate` : `/api/users/${userId}/activate`;
    setMessage('');
    try {
      await api.post(endpoint);
      setUser(prev => ({ ...prev, is_active: !prev.is_active }));
      flash('success', user.is_active ? 'User disabled — their sessions have ended' : 'User enabled');
    } catch (e) {
      flash('error', apiError(e, 'Failed to change status'));
    }
  };

  const isAdmin = user?.role === 'admin';
  const isSelf = me?.user_id === userId;
  const others = permissions.filter(p => !KNOWN.has(p));
  const permsDirty = user && JSON.stringify([...permissions].sort()) !== JSON.stringify([...(user.permissions || [])].sort());
  const msgType = message.startsWith('success:') ? 'success' : 'error';
  const msgText = message.replace(/^(success|error):/, '');

  if (loading) return <div className="p-6"><div className="h-4 bg-slate-100 rounded animate-pulse w-48" /></div>;
  if (!user) return (
    <div className="p-6">
      <Link to="/users" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-5">
        <ArrowLeft size={15} /> Back to Users
      </Link>
      <p className="text-slate-500">{loadError || 'User not found'}</p>
    </div>
  );

  return (
    <div className="p-6 max-w-2xl fade-in" data-testid="user-detail-page">
      <Link to="/users" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-5">
        <ArrowLeft size={15} /> Back to Users
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center">
            <span className="text-indigo-800 font-bold text-lg">{(user.first_name?.[0] || user.email[0]).toUpperCase()}{user.last_name?.[0]}</span>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>{user.full_name || user.email}</h1>
          <p className="text-sm text-slate-500">{user.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
              data-testid="user-status">
              {user.is_active ? 'Active' : 'Disabled'}
            </span>
            {isAdmin && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700">
                <Shield size={10} /> Administrator
              </span>
            )}
          </div>
        </div>
        {!isSelf && (
          <button
            onClick={toggleStatus}
            className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors flex-shrink-0 ${
              user.is_active
                ? 'border-red-200 text-red-600 hover:bg-red-50'
                : 'border-green-200 text-green-700 hover:bg-green-50'
            }`}
            data-testid="toggle-status-button"
          >
            {user.is_active ? 'Disable user' : 'Enable user'}
          </button>
        )}
      </div>

      {msgText && (
        <div role="status"
          className={`mb-4 text-sm px-3 py-2 rounded ${msgType === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
          data-testid="permissions-message">
          {msgText}
        </div>
      )}

      {/* Role */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="role-panel">
        <h2 className="font-semibold text-slate-900 text-sm mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>Role</h2>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <div className="relative">
              <select
                value={role}
                onChange={e => setRole(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900 appearance-none pr-8"
                data-testid="role-select"
                aria-label="Role"
              >
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          {role !== user.role && (
            <button
              onClick={saveRole}
              disabled={savingRole}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50 transition-colors"
              data-testid="save-role-button"
            >
              <Save size={13} />
              {savingRole ? 'Saving…' : 'Save role'}
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">{ROLES.find(r => r.value === role)?.description}</p>
        {isAdmin && role !== 'admin' && (
          <p className="text-xs text-amber-700 mt-2">
            Revokes administrator rights. Their permissions are cleared; assign the ones they need afterwards.
          </p>
        )}
        {isSelf && role !== user.role && (
          <p className="text-xs text-amber-700 mt-2">You are changing your own role.</p>
        )}
      </div>

      {/* Permissions */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="permissions-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Permissions</h2>
            <p className="text-xs text-slate-500 mt-0.5">What this user can reach. The server enforces it on every request.</p>
          </div>
          {isAdmin && (
            <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded font-medium">
              Administrator — everything
            </span>
          )}
        </div>

        {isAdmin ? (
          <p className="text-sm text-slate-600">Administrators hold every permission, including Administration → Users.</p>
        ) : (
          <>
            {grantsNothing(user.role, user.permissions) && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded-md mb-3"
                role="status" data-testid="no-access-assigned">
                {NO_ACCESS_HINT}
              </div>
            )}
            <div className="space-y-2">
              {PERMISSION_MODULES.map(mod => {
                const active = permissions.includes(mod.key);
                return (
                  <button
                    key={mod.key}
                    onClick={() => togglePerm(mod.key)}
                    role="checkbox"
                    aria-checked={active}
                    data-testid={`perm-toggle-${mod.key}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-md border text-left transition-all ${
                      active
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-900'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {active
                      ? <CheckSquare size={16} className="text-indigo-600 flex-shrink-0" />
                      : <Square size={16} className="text-slate-400 flex-shrink-0" />
                    }
                    <div>
                      <p className="text-sm font-medium">{mod.label}</p>
                      <p className="text-xs opacity-70">{mod.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {others.length > 0 && (
              <div className="mt-3" data-testid="other-permissions">
                <p className="text-xs font-medium text-slate-500 mb-1.5">Also held</p>
                <div className="flex flex-wrap gap-1.5">
                  {others.map(p => (
                    <span key={p} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-slate-100 text-xs text-slate-700">
                      {OTHER_LABELS[p] || p}
                      <button onClick={() => togglePerm(p)} className="p-0.5 rounded hover:bg-slate-200"
                        aria-label={`Remove ${OTHER_LABELS[p] || p}`}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 pt-4 border-t border-slate-100 mt-4">
              <button
                onClick={savePermissions}
                disabled={saving || !permsDirty}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50 transition-colors"
                data-testid="save-permissions-button"
              >
                <Save size={14} />
                {saving ? 'Saving...' : 'Save permissions'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Account */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-slate-500 mb-0.5">Added</p>
            <p className="font-mono text-slate-900 text-xs">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">Last sign-in</p>
            <p className="font-mono text-slate-900 text-xs">{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
